// Types
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
// Utils
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import { get } from 'lodash';
// Slices
import {
  createUnsavedReservationMode,
  deleteReservationMode,
  duplicateReservationModes,
  getDrawer,
  reservationModesLoading,
  reservationModesMapSelector,
  reservationModesSelector,
  selectedIdsSelector,
  setFirstDrawer,
  setSecondDrawer,
} from 'slices/reservationMode.slice';
import {
  fetchReservationTemplates,
  reservationTemplatesSelector,
  selectedReservationTemplateMap,
} from 'slices/reservationTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { fetchTimeRules } from 'slices/timeRule.slice';
import { arrayIncludes, matchString } from 'utils/stringUtils';
import { ETemplateType, type TReservationMode } from '@timeedit/types';
// Components
import {
  Button,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
} from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
//Styles
import './ReservationModesList.scss';

export interface IRowData {
  id: number;
  name: string;
  reservationTemplates: string;
}

interface Props {
  confirmDiscardChanges: (isFirstDrawer: boolean, actionFn: () => void) => void;
}

const ReservationModesList = ({ confirmDiscardChanges }: Props) => {
  const dispatch = useAppDispatch();

  const reservationMode: TReservationMode[] = useAppSelector(
    reservationModesSelector
  );
  const reservationModesMap = useAppSelector(reservationModesMapSelector);
  const reservationTemplateMap = useAppSelector(selectedReservationTemplateMap);
  const reservationTemplates = useAppSelector(reservationTemplatesSelector);

  const [highlightIds, setHighlightIds] = useState<number[]>([]);
  const selectedIds: number[] = useAppSelector(selectedIdsSelector);

  useEffect(() => {
    setTimeout(() => {
      setHighlightIds(selectedIds);
    }, 100);
  }, [selectedIds.join('-')]);

  const loading = useAppSelector(reservationModesLoading);

  const reservationTemplateMapOptions = useMemo(() => {
    return {
      type: reservationTemplates.map((i: any) => ({
        value: i.id,
        label: i.name,
      })),
      templatesType: Object.values(ETemplateType)
        .map((type) => ({
          value: type,
          label: getText(type),
        }))
        .sort((t1, t2) => t1.label.localeCompare(t2.label)),
    };
  }, [reservationTemplates]);

  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const filterData: IRowData[] = useMemo(() => {
    const dataSource = reservationMode.map((reservationItem) => {
      const reservationTemplates = reservationItem.reservationTemplates
        ? reservationItem.reservationTemplates
            .map((i: number) => get(reservationTemplateMap, [i, 'name'], ''))
            .filter(Boolean)
            .join(', ')
        : '';
      const templatesTypes: string[] = reservationItem.reservationTemplates
        ? reservationItem.reservationTemplates
            .map((i: number) => reservationTemplateMap[i]?.templateType)
            .filter(Boolean)
        : [];

      return {
        id: reservationItem.id,
        name: reservationItem.name || 'New reservation mode',
        reservationTemplates,
        templatesTypes: Array.from(new Set(templatesTypes)),
        reservationTemplatesIds: reservationItem.reservationTemplates,
      };
    });
    const {
      filters: { type, templatesType },
      freeTextSearch,
    } = filterSettings;

    return dataSource.filter((item) => {
      const matchesFreeText =
        !freeTextSearch || matchString(item.name, freeTextSearch);
      const matchesType =
        !type || arrayIncludes(item.reservationTemplatesIds, type);
      const matchesTemplatesType =
        !templatesType || arrayIncludes(item.templatesTypes, templatesType);

      return matchesFreeText && matchesType && matchesTemplatesType;
    });
  }, [reservationMode, reservationTemplateMap, filterSettings]);

  const onCreate = useCallback(() => {
    confirmDiscardChanges(true, () => dispatch(createUnsavedReservationMode()));
  }, []);

  const onDuplicate = useCallback(
    (record: IRowData) => {
      confirmDiscardChanges(true, () =>
        dispatch(duplicateReservationModes(reservationModesMap[record.id]))
      );
    },
    [confirmDiscardChanges, dispatch, reservationModesMap]
  );

  const onCompare = useCallback(
    (record: IRowData) => {
      const firstDrawer = dispatch(getDrawer(true));
      if (!firstDrawer) {
        return dispatch(setFirstDrawer(reservationModesMap[record.id]));
      }
      confirmDiscardChanges(false, () =>
        dispatch(setSecondDrawer(reservationModesMap[record.id]))
      );
    },
    [confirmDiscardChanges, dispatch, reservationModesMap]
  );

  const onRowClick = useCallback(
    (record: IRowData) => {
      const firstDrawer = dispatch(getDrawer(true));
      if (!firstDrawer) {
        dispatch(setFirstDrawer(reservationModesMap[record.id]));
      } else {
        confirmDiscardChanges(true, () =>
          dispatch(setFirstDrawer(reservationModesMap[record.id]))
        );
      }
    },
    [confirmDiscardChanges, dispatch, reservationModesMap]
  );

  const onRow = useRowProps(onRowClick, highlightIds);

  const onDelete = useCallback(
    (record: IRowData) =>
      confirmDelete(record.name, () =>
        dispatch(deleteReservationMode({ reservationModeId: record.id }))
      ),
    [confirmDelete, dispatch]
  );

  const checkDrawerOpen = useCallback(
    () => Boolean(dispatch(getDrawer(true))),
    [dispatch]
  );

  const menuProps = useCallback(
    (record: IRowData) =>
      getMenuProps<IRowData>({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, checkDrawerOpen]
  );

  const footer = useCallback(
    () => (
      <Space>
        <Button size="small" type="primary" onClick={onCreate}>
          Create new
        </Button>
      </Space>
    ),
    []
  );

  const title = useTableTitle<IRowData>({
    total: reservationMode.length,
    filteredAmount: filterData.length,
  });

  useEffect(() => {
    dispatch(fetchReservationTemplates());
    dispatch(fetchTimeRules());
  }, []);

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  return (
    <div className="padding-rule-list" ref={contentRef}>
      <Filter
        defaultFields={defaultFields}
        options={reservationTemplateMapOptions}
        settings={filterSettings}
        onChange={setFilterSettings}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={filterData}
          columns={columns}
          getMenuProps={menuProps}
          onRow={onRow}
          footer={footer}
          title={title}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      {contextHolder}
    </div>
  );
};

export default memo(ReservationModesList);

const columns = [
  {
    key: 'name',
    dataIndex: 'name',
    title: 'Name',
    ellipsis: true,
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.name.localeCompare(item2.name),
  },
  {
    key: 'reservationTemplates',
    dataIndex: 'reservationTemplates',
    title: 'Reservation Templates',
    ellipsis: true,
  },
  {
    key: 'templatesTypes',
    dataIndex: 'templatesTypes',
    title: 'Reservation Type',
    render: (text: string[]) => text.map(getText).join(', '),
    ellipsis: true,
  },
];

const defaultFields: TEIFilterField[] = [
  {
    label: 'Reservation type',
    key: 'templatesType',
    type: TEEFilterType.text,
  },
  {
    label: 'Reservation Templates',
    key: 'type',
    type: TEEFilterType.text,
  },
];
